import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Avatar, Pagination, LinearProgress } from "@mui/material";
import HoverTooltip from "./HoverTooltip";
import { Add, Edit, Delete, CheckCircle, Comment, Schedule, PersonAddAlt } from '@mui/icons-material';
import styled from "@emotion/styled";
import { getObjectActivity, getUserActivity, getAccountActivity, getClientActivity } from "../redux/ducks/activity";
import { formatTimeAgo, formatDateTime } from "../utils/dateUtils";
import { resolveActivityLink } from "../utils/activityNavigation";

const PAGE_SIZE = 25;
const MAX_LOG_ITEMS = 250;

const ActivityList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const ActivityItem = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'clickable',
})(({ theme, clickable }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: clickable ? 'pointer' : 'default',
    '&:last-child': {
        borderBottom: 'none',
    },
    ...(clickable && {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        borderRadius: theme.shape.borderRadius,
        marginLeft: theme.spacing(-1),
        marginRight: theme.spacing(-1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    }),
}));

const ActivityIcon = styled(Avatar)(({ theme, activitytype }) => ({
    width: 32,
    height: 32,
    marginRight: theme.spacing(1.5),
    backgroundColor:
        activitytype === 'create' ? theme.palette.success.main :
        activitytype === 'update' ? theme.palette.info.main :
        activitytype === 'delete' ? theme.palette.error.main :
        activitytype === 'complete' ? theme.palette.success.main :
        activitytype === 'comment' ? theme.palette.primary.main :
        activitytype === 'time' ? theme.palette.secondary.main :
        activitytype === 'assign' ? theme.palette.warning.main :
        theme.palette.grey[500],
}));

const ActivityContent = styled(Box)({
    flex: 1,
    minWidth: 0,
});

const ActivityText = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
}));

const ActivityTime = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    marginTop: 2,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const TabBox = styled(Box)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[3],
}));

const PaginationRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
}));

const TopProgress = styled(LinearProgress)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const getActivityIcon = (category) => {
    switch (category) {
        case 'create':
        case 'add':
            return <Add fontSize="small" />;
        case 'update':
        case 'edit':
            return <Edit fontSize="small" />;
        case 'delete':
        case 'remove':
            return <Delete fontSize="small" />;
        case 'complete':
            return <CheckCircle fontSize="small" />;
        case 'comment':
            return <Comment fontSize="small" />;
        case 'time':
            return <Schedule fontSize="small" />;
        case 'assign':
            return <PersonAddAlt fontSize="small" />;
        default:
            return <Edit fontSize="small" />;
    }
};

const ActivityLog = ({ objectId, objectType, fallbackAccountSlug }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [activities, setActivities] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setPage(1);
    }, [objectId, objectType]);

    const fetchPage = useCallback((pageNum) => {
        if (!objectId || !objectType) {
            setActivities([]);
            setTotalPages(1);
            setLoading(false);
            return;
        }

        setLoading(true);

        const callback = (result) => {
            const rawList = result?.activities || result || [];
            const activityList = [];

            rawList.forEach((activity) => {
                activityList.push({
                    id: activity._id,
                    raw: activity,
                    displayCategory: activity.displayCategory || 'default',
                    text: activity.description,
                    time: activity.createdAt,
                    user: activity.user?.fullName || '',
                    linkTo: resolveActivityLink(activity, { fallbackAccountSlug }),
                });
            });

            activityList.sort((a, b) => new Date(b.time) - new Date(a.time));

            const apiTotal = typeof result?.totalActivities === 'number' ? result.totalActivities : activityList.length;
            const cappedTotal = Math.min(apiTotal, MAX_LOG_ITEMS);
            const pages = Math.max(1, Math.ceil(cappedTotal / PAGE_SIZE));

            setActivities(activityList);
            setTotalPages(pages);
            setLoading(false);
        };

        const query = { callback, page: pageNum, limit: PAGE_SIZE };

        switch (objectType) {
            case "user":
                dispatch(getUserActivity(objectId, query));
                break;
            case "account":
                dispatch(getAccountActivity(objectId, query));
                break;
            case "client":
                dispatch(getClientActivity(objectId, query));
                break;
            default:
                dispatch(getObjectActivity(objectType, objectId, query));
        }
    }, [dispatch, objectId, objectType, fallbackAccountSlug]);

    useEffect(() => {
        fetchPage(page);
    }, [fetchPage, page]);

    const handleRowActivate = (linkTo) => {
        if (linkTo) {
            navigate(linkTo);
        }
    };

    return (
        <TabBox>
            {loading && <TopProgress />}
            {(activities || []).length > 0 ? (
                <>
                    <ActivityList>
                        {(activities || []).map((activity, index) => (
                            <ActivityItem
                                key={activity.id || index}
                                clickable={Boolean(activity.linkTo)}
                                onClick={() => handleRowActivate(activity.linkTo)}
                                onKeyDown={(e) => {
                                    if (activity.linkTo && (e.key === 'Enter' || e.key === ' ')) {
                                        e.preventDefault();
                                        handleRowActivate(activity.linkTo);
                                    }
                                }}
                                role={activity.linkTo ? 'link' : undefined}
                                tabIndex={activity.linkTo ? 0 : undefined}
                            >
                                <ActivityIcon activitytype={activity.displayCategory}>
                                    {getActivityIcon(activity.displayCategory)}
                                </ActivityIcon>
                                <ActivityContent>
                                    <ActivityText>
                                        {activity.user} - {activity.text}
                                    </ActivityText>
                                    <HoverTooltip title={formatDateTime(activity.time)} placement="bottom-start">
                                        <ActivityTime>
                                            {formatTimeAgo(activity.time)}
                                        </ActivityTime>
                                    </HoverTooltip>
                                </ActivityContent>
                            </ActivityItem>
                        ))}
                    </ActivityList>
                    {totalPages > 1 && (
                        <PaginationRow>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                color="primary"
                                size="small"
                                disabled={loading}
                            />
                        </PaginationRow>
                    )}
                </>
            ) : !loading ? (
                <EmptyState>
                    <Typography variant="body2">
                        No recent activity
                    </Typography>
                </EmptyState>
            ) : null}
        </TabBox>
    );
};

export default ActivityLog;
